import * as Ariakit from '@ariakit/react';
import { useQuery, type QueryKey, type UseQueryOptions } from '@tanstack/react-query';
import cn from 'classnames';
import { matchSorter } from 'match-sorter';
import { startTransition, useEffect, useMemo, useState, type ReactElement } from 'react';
import { SelectButton } from '#components/atoms/buttons/select-button/SelectButton.tsx';
import { Divider } from '#components/atoms/divider/Divider.tsx';
import { HelpTip } from '#components/atoms/forms/help-tip/HelpTip.tsx';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { SelectItemLoading } from '#components/patterns/forms/select/select-item-loading/SelectItemLoading.tsx';
import { SelectItem } from '#components/patterns/forms/select/select-item/SelectItem.tsx';
import { SelectProvider } from '#components/patterns/forms/select/SelectContext.tsx';
import { SelectMenu } from '../select-menu/SelectMenu.tsx';
import type { Select } from '../select/Select.tsx';
import styles from './SelectAsyncSearchable.module.css';

export type Item = {
  value: string;
  label: React.ReactNode;
  hint?: string;
};

const DEFAULT_PLACEHOLDER = 'Please select…';
const DEFAULT_SEARCHKEYS = ['label'];
const DEFAULT_INITIAL_ITEMS_LIMIT = 50;

type Props<T extends string, TQueryData, TQueryError extends Error, TQueryKey extends QueryKey> = {
  children: (items: Item[]) => React.ReactNode;
  /**
   * A react hook which returns the result of a call to react-query,
   * with data being returned as an array of `items`.
   *
   * The query is made immediately upon mount, not lazily upon dropdown open.
   */
  getItemsQueryOptions: UseQueryOptions<TQueryData, TQueryError, { items: Item[] }, TQueryKey>;
  /** Any items provided here will be rendered at the bottom of the matches, even if no matches are found */
  additionalItems?: Item[];
  /** Plural name of options being shown, used in various status messages. */
  optionsName?: string;
  /** The item with a matching `value` property will be the default selected item, if found */
  defaultValue?: string | null;
  /** an onChange e.g. from react-hook-form */
  onChange?: (value: string) => void;
  /** What key of items should be used for search */
  searchKeys?: string[];
  /** Props to pass to Ariakit's useComboboxStore */
  storeProps?: Ariakit.ComboboxStoreProps;
  /** Props to pass to the menu component */
  menuProps?: Ariakit.SelectPopoverProps;
  /** If there are fewer than this number items in the menu, hide the search box.  Defaults to 7, set to 0 to always show */
  showSearchMin?: number;
  /** When there is no search term, render at most this many options initially */
  initialItemsLimit?: number;
} & Omit<React.ComponentProps<typeof Select<T>>, 'children' | 'storeProps'>;

export function SelectAsyncSearchable<
  T extends string,
  TQueryData,
  TQueryError extends Error,
  TQueryKey extends QueryKey,
>({ innerRef, ...props }: Props<T, TQueryData, TQueryError, TQueryKey>) {
  const [searchValue, setSearchValue] = useState<string>('');

  const {
    optionsName = 'options',
    placeholder = DEFAULT_PLACEHOLDER,
    // Workaround to https://github.com/ariakit/ariakit/issues/2516
    defaultValue = '',
    searchKeys = DEFAULT_SEARCHKEYS,
    showSearchMin = 7,
    initialItemsLimit = DEFAULT_INITIAL_ITEMS_LIMIT,
  } = props;
  const combobox = Ariakit.useComboboxStore({
    resetValueOnHide: true,
    setValue: (value) => {
      startTransition(() => {
        setSearchValue(value);
      });
    },
    ...props.storeProps,
  });
  const select = Ariakit.useSelectStore({ defaultValue, combobox });
  const isOpen = Ariakit.useStoreState(select, 'open');
  const value = Ariakit.useStoreState(select, 'value') as T; // Can't figure out any better way right now
  const { data: { items = [] } = {}, isFetching, error } = useQuery(props.getItemsQueryOptions);
  const showSearch = isOpen && items.length >= showSearchMin;
  const isWaitingForInitialItems = isFetching && items.length === 0;

  // Call onChange whenever value changes, to pick up on changes due to key presses.
  // It will also fire a dupe when an option is clicked, but that seems fine.
  const { onChange } = props;
  useEffect(() => {
    if (onChange) {
      onChange(value);
    }
  }, [value, onChange]);

  /* eslint-disable react-hooks/preserve-manual-memoization -- React Compiler thinks that `searchKeys` can be mutated as it is an array of strings. */
  const visibleItems = useMemo(() => {
    if (!searchValue) {
      return items.slice(0, initialItemsLimit);
    }

    return matchSorter(items, searchValue, {
      baseSort: (a, b) => (a.index < b.index ? -1 : 1),
      keys: searchKeys,
    });
  }, [searchValue, items, initialItemsLimit, searchKeys]);
  /* eslint-enable react-hooks/preserve-manual-memoization */
  const isTruncated = !searchValue && items.length > initialItemsLimit;

  let menu: ReactElement | null = null;
  if (isWaitingForInitialItems) {
    menu = <SelectItemLoading>Loading {optionsName}…</SelectItemLoading>;
  } else if (error instanceof Error) {
    menu = (
      <SelectItem isMessage={true}>
        <ErrorMessage>{error.message}</ErrorMessage>
      </SelectItem>
    );
  } else if (visibleItems.length) {
    menu = (
      <>
        {props.children(visibleItems)}
        {isTruncated ?
          <>
            <Divider className="-ml-8 -mr-8" />
            <SelectItem isMessage={true}>Showing first {initialItemsLimit}. Type to narrow results.</SelectItem>
          </>
        : null}
        {props.additionalItems ?
          <>
            <Divider className="-ml-8 -mr-8" />
            {props.children(props.additionalItems)}
          </>
        : null}
      </>
    );
  } else {
    menu = (
      <>
        <SelectItem isMessage={true}>No {optionsName} found</SelectItem>
        {props.additionalItems ?
          <>
            <Divider className="-ml-8 -mr-8" />
            {props.children(props.additionalItems)}
          </>
        : null}
      </>
    );
  }

  // `additionalItems` are selectable but never come back from the query, so resolving the
  // trigger's label and filled styling has to search them alongside the fetched items.
  const selectedItem = (props.additionalItems ? [...items, ...props.additionalItems] : items).find(
    (itm) => itm.value === value
  );

  return (
    <Ariakit.ComboboxProvider store={combobox}>
      <SelectProvider store={select}>
        <Field
          className={props.className}
          label={
            <div className={cn('label flex align-baseline', { 'visually-hidden': props.visuallyHideLabel })}>
              <Ariakit.SelectLabel store={select}>{props.label}</Ariakit.SelectLabel>
              {props.helpTip ?
                <HelpTip>{props.helpTip}</HelpTip>
              : null}
            </div>
          }
        >
          {props.help}
          <div>
            <Ariakit.Select
              store={select}
              className="select"
              ref={innerRef}
              disabled={props.disabled}
              onBlur={props.onBlur}
              render={
                <SelectButton
                  hasValue={Boolean(selectedItem?.label)}
                  hasError={props.hasError ?? Boolean(props.errorMessage)}
                  isMenuOpen={isOpen}
                />
              }
            >
              {selectedItem?.label ?? placeholder}
            </Ariakit.Select>
            <SelectMenu
              {...props.menuProps}
              type="select"
              className={styles.SelectAsyncSearchable_popover}
              store={select}
            >
              <div className={showSearch ? styles.SelectAsyncSearchable_searchWrapper : 'visually-hidden'}>
                <Ariakit.Combobox
                  autoSelect
                  inert={!showSearch && !isWaitingForInitialItems}
                  placeholder="Search..."
                  className="combobox"
                  aria-label="Search"
                  render={<Input className="width-100" />}
                />
              </div>
              <Ariakit.ComboboxList>{menu}</Ariakit.ComboboxList>
            </SelectMenu>
          </div>
          {props.errorMessage && <ErrorMessage>{props.errorMessage}</ErrorMessage>}
          {props.fieldChildren}
        </Field>
      </SelectProvider>
    </Ariakit.ComboboxProvider>
  );
}
